import type { OtherTestFormData } from '../../../graphql';
import {
  type FormController,
  FormModificationMode,
} from '../../../../../utils';

export type OtherTestControl = Record<keyof OtherTestFormData, boolean> & {
  enableSave: boolean;
  enableDelete: boolean;
  enableSaveCondition: boolean;
};

export type OtherTestFieldPermissions = Omit<OtherTestControl, 'otherTestId'>;

// Only records with "I will update result at xxx" can be updated.
export const editOtherTestPermission = (
  values: OtherTestFormData,
  formikValues: OtherTestFormData,
  control: FormController,
): OtherTestFieldPermissions => {
  const { formModificationMode } = control;
  const { resultAnnouncedIndicator } = values;

  // Other Test form field permissions
  const disableOtherTestFieldPermissions: OtherTestFieldPermissions = {
    testType: false,
    testDate: false,
    resultAnnouncedIndicator: false,
    expectedResultUpdateDate: false,
    totalScore: false,
    totalPercentile: false,
    quantitativeScore: false,
    quantitativePercentile: false,
    analyticalWritingScore: false,
    analyticalWritingPercentile: false,
    verbalScore: false,
    verbalPercentile: false,
    integratedReasoningScore: false,
    integratedReasoningPercentile: false,
    dataInsightsScore: false,
    dataInsightsPercentile: false,
    supportingDocuments: false,
    referenceNo: false,
    resultReceiptDate: false,
    enableSave: false,
    enableSaveCondition: false,
    enableDelete: false,
  };

  const enableOtherTestFieldPermissions: OtherTestFieldPermissions = {
    ...Object.assign(
      {},
      ...Object.keys(disableOtherTestFieldPermissions).map(key => ({
        [key]: true,
      })),
    ),
    resultAnnouncedIndicator: Boolean(formikValues.testType),
    enableSaveCondition:
      control.formModificationMode === FormModificationMode.CONDITION_MEET,
  };

  const modifyOtherTestFieldPermissions: OtherTestFieldPermissions = {
    ...(resultAnnouncedIndicator // For those applicant who have not announced their result, they can update result fields.
      ? disableOtherTestFieldPermissions
      : enableOtherTestFieldPermissions),
    testType: false,
    enableDelete: false,
  };

  const otherTestPermission: Record<
    FormModificationMode,
    OtherTestFieldPermissions
  > = {
    [FormModificationMode.ENABLE]: enableOtherTestFieldPermissions,
    [FormModificationMode.DISABLE]: disableOtherTestFieldPermissions,
    [FormModificationMode.MODIFIED_ONLY]: modifyOtherTestFieldPermissions,
    [FormModificationMode.CONDITION_MEET]: {
      ...enableOtherTestFieldPermissions,
      // In meet condition situation, not allow the applicant to delete
      // only allow them to revise/add
      enableDelete: false,
    },
  };

  return otherTestPermission[formModificationMode];
};
